import { BlockHolder } from '@alfa-bank/site-block-holder';
import { ImgV2 as Img } from '@alfa-bank/site-img-v2';
import type { FC } from 'react';
import React, { useEffect, useMemo } from 'react';
import { Aligner } from '@alfa-bank/site-aligner';
import { sendEvent } from '@alfa-bank/site-helpers-common/browser/send-event';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from '@alfa-bank/site-link';
import cn from 'classnames';
import { selectAppData, setStep } from '@alfa-bank/site-dc-form-api/store';

import { formatPhoneValue } from '../../utils/helpers';
import { errorStepAnalytics as analytics } from '../../analytics';

import { getStatus } from './utils';
import type { TError } from './models';

const renderPhone = (stringWithPhone: string) => {
    const pattern = /\+?\d[\d\s]{1,11}/g;
    const splitText = stringWithPhone.match(pattern);

    return splitText ? splitText.join('') : stringWithPhone;
};

export const Error: FC<TError> = ({
    Block,
    Button,
    DtClient,
    Text,
    className,
    errors,
    styles,
    titleView,
}) => {
    const dispatch = useDispatch();
    const { status } = useSelector(selectAppData);

    const errorStatus = getStatus(status);
    const errorScreen = errors[errorStatus];

    useEffect(() => {
        if (errorStatus && analytics) {
            sendEvent(analytics[errorStatus]);
        }
    });

    const content = useMemo(() => {
        const isNotDtClient =
            'title' in errorScreen &&
            'subtitle' in errorScreen &&
            'img' in errorScreen &&
            'buttonText' in errorScreen;

        if (!isNotDtClient) {
            return <DtClient {...errors.DT_CLIENT} />;
        }

        const getPhone = (tel: string) => {
            const preparedTel = tel.slice(0, 2) === '+7' ? `8${tel.slice(2)}` : tel;

            return `tel:${formatPhoneValue(preparedTel)}`;
        };

        return (
            <>
                <BlockHolder alignX="center" alignY="top" height={'160px'}>
                    <Img dataTestId="error-img" height={160} src={errorScreen.img} width={160} />
                </BlockHolder>
                <Block notPadded styler={{ paddingTop: 24 }}>
                    <Aligner>
                        <Text
                            dataTestId="error-title"
                            styler={{ padding: 0, margin: 0 }}
                            view={titleView}
                        >
                            {errorScreen.title}
                        </Text>

                        <Text
                            dataTestId="error-subtitle"
                            styler={{ padding: 0, paddingTop: 24, margin: 0 }}
                            view="primary-medium"
                        >
                            {errorScreen.subtitle}
                        </Text>

                        {errorScreen.moscowPhoneNumber && (
                            <Text
                                styler={{ padding: 0, paddingTop: 24, margin: 0 }}
                                view="primary-medium"
                            >
                                <Link
                                    border="dashed"
                                    dataTestId="error-moscow-number"
                                    href={getPhone(errorScreen.moscowPhoneNumber)}
                                >
                                    {renderPhone(errorScreen.moscowPhoneNumber)}
                                </Link>
                            </Text>
                        )}

                        {errorScreen.regionPhoneNumber && (
                            <Text
                                dataTestId="error-region-number"
                                styler={{ padding: 0, margin: 0 }}
                                view="primary-medium"
                            >
                                <Link
                                    border="dashed"
                                    href={getPhone(errorScreen.regionPhoneNumber)}
                                >
                                    {renderPhone(errorScreen.regionPhoneNumber)}
                                </Link>
                            </Text>
                        )}

                        <Button
                            dataTestId="error-button"
                            href={errorScreen.buttonHref}
                            onClick={
                                errorScreen.buttonHref
                                    ? undefined
                                    : () => dispatch(setStep('firstStep'))
                            }
                            size="s"
                            styler={{ paddingTop: 24 }}
                            view="primary"
                        >
                            {errorScreen.buttonText}
                        </Button>
                    </Aligner>
                </Block>
            </>
        );
    }, [Block, Button, DtClient, Text, dispatch, errorScreen, errors.DT_CLIENT, titleView]);

    return (
        <div className={className}>
            <div className={cn(styles.external, styles.error)}>{content}</div>
        </div>
    );
};
